// Vanilla JavaScript Sticky Plugin
// Makes an element on the page stick on the screen as you scroll

class StickyJS {
  constructor() {
    this.defaults = {
      topSpacing: 0,
      bottomSpacing: 0,
      className: 'is-sticky',
      wrapperClassName: 'sticky-wrapper',
      center: false,
      getWidthFrom: '',
      widthFromWrapper: true,
      responsiveWidth: false,
      zIndex: 'inherit'
    };
    
    this.sticked = [];
    this.windowHeight = window.innerHeight;
    
    // Bind methods to maintain context
    this.scroller = this.scroller.bind(this);
    this.resizer = this.resizer.bind(this);
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Initial scroll check
    setTimeout(() => this.scroller(), 0);
  }
  
  setupEventListeners() {
    window.addEventListener('scroll', this.scroller, false);
    window.addEventListener('resize', this.resizer, false);
  }
  
  scroller() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const atbContainers = document.querySelectorAll('.atb-container');
    const scrollBottom = scrollTop + window.innerHeight;
    const documentBottom = document.documentElement.scrollHeight - 100; // 100px tolerance

    if (scrollBottom >= documentBottom) {
      atbContainers.forEach(el => el.style.display = 'none');
    } else {
      atbContainers.forEach(el => el.style.display = '');
    }
    const documentHeight = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.clientHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    );
    const dwh = documentHeight - this.windowHeight;
    const extra = (scrollTop > dwh) ? dwh - scrollTop : 0;

    for (let i = 0; i < this.sticked.length; i++) {
      const s = this.sticked[i];
      const elementTop = this.getOffset(s.stickyWrapper).top;
      const etse = elementTop - s.topSpacing - extra;

      // Update height in case of dynamic content
      s.stickyWrapper.style.height = this.getOuterHeight(s.stickyElement) + 'px';

      if (scrollTop <= etse) {
        if (s.currentTop !== null) {
          this.resetElementStyles(s.stickyElement);
          s.stickyElement.parentElement.classList.remove(s.className);
          this.triggerEvent(s.stickyElement, 'sticky-end', s);
          s.currentTop = null;
        }
      } else {
        let newTop = documentHeight - this.getOuterHeight(s.stickyElement)
          - s.topSpacing - s.bottomSpacing - scrollTop - extra;
        
        if (newTop < 0) {
          newTop = newTop + s.topSpacing;
        } else {
          newTop = s.topSpacing;
        }
        
        if (s.currentTop !== newTop) {
          let newWidth;
          
          if (s.getWidthFrom) {
            const targetElement = typeof s.getWidthFrom === 'string' 
              ? document.querySelector(s.getWidthFrom) 
              : s.getWidthFrom;
            if (targetElement) {
              const padding = this.getInnerWidth(s.stickyElement) - s.stickyElement.clientWidth;
              newWidth = targetElement.clientWidth - padding;
            }
          } else if (s.widthFromWrapper) {
            newWidth = s.stickyWrapper.clientWidth;
          }
          
          if (newWidth == null) {
            newWidth = s.stickyElement.clientWidth;
          }
          
          this.setElementStyles(s.stickyElement, {
            width: newWidth + 'px',
            position: 'fixed',
            top: newTop + 'px',
            zIndex: s.zIndex
          });
          
          s.stickyElement.parentElement.classList.add(s.className);
          
          if (s.currentTop === null) {
            this.triggerEvent(s.stickyElement, 'sticky-start', s);
          } else {
            this.triggerEvent(s.stickyElement, 'sticky-update', s);
          }
          
          if ((s.currentTop === s.topSpacing && s.currentTop > newTop) || 
              (s.currentTop === null && newTop < s.topSpacing)) {
            this.triggerEvent(s.stickyElement, 'sticky-bottom-reached', s);
          } else if (s.currentTop !== null && newTop === s.topSpacing && s.currentTop < newTop) {
            this.triggerEvent(s.stickyElement, 'sticky-bottom-unreached', s);
          }
          
          s.currentTop = newTop;
        }
        
        // Check if sticky has reached end of container
        const stickyWrapperContainer = s.stickyWrapper.parentElement;
        const stickyElementOffset = this.getOffset(s.stickyElement);
        const containerOffset = this.getOffset(stickyWrapperContainer);
        
        const unstick = (stickyElementOffset.top + this.getOuterHeight(s.stickyElement) >= 
                        containerOffset.top + this.getOuterHeight(stickyWrapperContainer)) && 
                       (stickyElementOffset.top <= s.topSpacing);
        
        if (unstick) {
          this.setElementStyles(s.stickyElement, {
            position: 'absolute',
            top: '',
            bottom: '0px',
            zIndex: ''
          });
        } else {
          this.setElementStyles(s.stickyElement, {
            position: 'fixed',
            top: newTop + 'px',
            bottom: '',
            zIndex: s.zIndex
          });
        }
      }
    }
  }
  
  resizer() {
    this.windowHeight = window.innerHeight;
    
    for (let i = 0; i < this.sticked.length; i++) {
      const s = this.sticked[i];
      let newWidth = null;
      
      if (s.getWidthFrom) {
        if (s.responsiveWidth) {
          const targetElement = typeof s.getWidthFrom === 'string' 
            ? document.querySelector(s.getWidthFrom) 
            : s.getWidthFrom;
          if (targetElement) {
            newWidth = targetElement.clientWidth;
          }
        }
      } else if (s.widthFromWrapper) {
        newWidth = s.stickyWrapper.clientWidth;
      }
      
      if (newWidth != null) {
        s.stickyElement.style.width = newWidth + 'px';
      }
    }
  }
  
  init(elements, options = {}) {
    const config = { ...this.defaults, ...options };
    
    elements.forEach(element => {
      const stickyElement = element;
      const stickyId = stickyElement.id;
      const wrapperId = stickyId ? 
        stickyId + '-' + this.defaults.wrapperClassName : 
        this.defaults.wrapperClassName;
      
      // Check if already wrapped
      if (stickyElement.parentElement && 
          stickyElement.parentElement.id === wrapperId) {
        return;
      }
      
      const wrapper = document.createElement('div');
      wrapper.id = wrapperId;
      wrapper.className = config.wrapperClassName;
      
      stickyElement.parentNode.insertBefore(wrapper, stickyElement);
      wrapper.appendChild(stickyElement);
      
      const stickyWrapper = stickyElement.parentElement;
      
      if (config.center) {
        const width = this.getOuterWidth(stickyElement);
        stickyWrapper.style.width = width + 'px';
        stickyWrapper.style.marginLeft = 'auto';
        stickyWrapper.style.marginRight = 'auto';
      }
      
      if (getComputedStyle(stickyElement).float === 'right') {
        stickyElement.style.float = 'none';
        stickyWrapper.style.float = 'right';
      }
      
      const stickyConfig = {
        ...config,
        stickyElement,
        stickyWrapper,
        currentTop: null
      };
      
      this.sticked.push(stickyConfig);
      this.setWrapperHeight(stickyElement);
      this.setupChangeListeners(stickyElement);
    });
    
    return this;
  }
  
  setWrapperHeight(stickyElement) {
    const stickyWrapper = stickyElement.parentElement;
    if (stickyWrapper) {
      stickyWrapper.style.height = this.getOuterHeight(stickyElement) + 'px';
    }
  }
  
  setupChangeListeners(stickyElement) {
    if (window.MutationObserver) {
      const mutationObserver = new MutationObserver(mutations => {
        if (mutations[0].addedNodes.length || mutations[0].removedNodes.length) {
          this.setWrapperHeight(stickyElement);
        }
      });
      mutationObserver.observe(stickyElement, { subtree: true, childList: true });
    } else {
      // Fallback for older browsers
      stickyElement.addEventListener('DOMNodeInserted', () => {
        this.setWrapperHeight(stickyElement);
      }, false);
      stickyElement.addEventListener('DOMNodeRemoved', () => {
        this.setWrapperHeight(stickyElement);
      }, false);
    }
  }
  
  update() {
    this.scroller();
    return this;
  }
  
  unstick(elements) {
    elements.forEach(element => {
      const removeIdx = this.sticked.findIndex(s => s.stickyElement === element);
      
      if (removeIdx !== -1) {
        this.sticked.splice(removeIdx, 1);
        
        const wrapper = element.parentElement;
        const parent = wrapper.parentElement;
        
        parent.insertBefore(element, wrapper);
        parent.removeChild(wrapper);
        
        this.resetElementStyles(element);
        element.style.float = '';
      }
    });
    
    return this;
  }
  
  // Utility methods
  getOffset(element) {
    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    return {
      top: rect.top + scrollTop,
      left: rect.left + scrollLeft
    };
  }
  
  getOuterHeight(element) {
    const style = getComputedStyle(element);
    return element.offsetHeight + 
           parseFloat(style.marginTop) + 
           parseFloat(style.marginBottom);
  }
  
  getOuterWidth(element) {
    const style = getComputedStyle(element);
    return element.offsetWidth + 
           parseFloat(style.marginLeft) + 
           parseFloat(style.marginRight);
  }
  
  getInnerWidth(element) {
    const style = getComputedStyle(element);
    return element.clientWidth + 
           parseFloat(style.paddingLeft) + 
           parseFloat(style.paddingRight);
  }
  
  setElementStyles(element, styles) {
    Object.keys(styles).forEach(key => {
      element.style[key] = styles[key];
    });
  }
  
  resetElementStyles(element) {
    element.style.width = '';
    element.style.position = '';
    element.style.top = '';
    element.style.zIndex = '';
  }
  
  triggerEvent(element, eventName, data) {
    const event = new CustomEvent(eventName, { detail: data });
    element.dispatchEvent(event);
  }
}

// Initialize and expose the API immediately
(function() {
  // Create global instance
  const stickyJS = new StickyJS();

  // Public API - attach to window immediately
  window.StickyJS = {
    init: function(selector, options) {
      const elements = typeof selector === 'string' 
        ? Array.from(document.querySelectorAll(selector))
        : Array.isArray(selector) ? selector : [selector];
      return stickyJS.init(elements, options);
    },
    
    unstick: function(selector) {
      const elements = typeof selector === 'string' 
        ? Array.from(document.querySelectorAll(selector))
        : Array.isArray(selector) ? selector : [selector];
      return stickyJS.unstick(elements);
    },
    
    update: function() {
      return stickyJS.update();
    }
  };

  // Also create a simpler global function for direct use
  window.initSticky = function(selector, options) {
    return window.StickyJS.init(selector, options);
  };

  // Initialize when DOM is ready
  function initializeDefault() {
    // Check if .atb-container exists before trying to initialize
    const containers = document.querySelectorAll('.atb-container');
    if (containers.length > 0) {
      window.StickyJS.init('.atb-container', { zIndex: 9999999, getWidthFrom: 'body' });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeDefault);
  } else {
    // DOM is already ready
    initializeDefault();
  }
})();